/**
 * FleetOverviewWidget — Vue graphique flotte + chauffeurs sur Dashboard.
 *
 * 05/02/2026 — Q1 retour user prod : ajout d'une carte synthèse visuelle
 * directement sous "Revenus du mois / Charges du mois" qui montre :
 *   • Tous les véhicules de la flotte avec leur image, état coloré (idle /
 *     active / maintenance / broken / delivery), pastille de statut.
 *   • Tous les chauffeurs avec photo + initiale, état coloré (idle / active /
 *     training / resting / sick / vacation), pastille de statut.
 *
 * 08/02/2026 — Q2 user prod refinement :
 *   • Véhicules désormais GROUPÉS par tier (T1 → T5), chaque groupe ayant
 *     un séparateur titré « TIER N — N camion(s) » → on n'a plus besoin
 *     d'afficher la pastille de tier sur chaque carte (gain de lisibilité).
 *   • Chauffeurs groupés par permis principal (B / C / CE) avec la même
 *     logique de séparateur.
 *   • Encart agrandissable progressivement : par défaut on affiche la
 *     première rangée de chaque section, un bouton « Voir tout » étend.
 *
 * Trois grandes catégories visuelles :
 *   1. **Disponible**         → bordure verte, image pleine couleur.
 *   2. **En mission**         → bordure ambre + badge contrat.
 *   3. **Indisponible**       → bordure rouge/grisée, image opacity réduite,
 *                                pastille STOP (icône Ban).
 *   4. **En formation** (chauffeurs uniquement) → bordure bleue, badge GC.
 */
import React, { useState, useMemo } from 'react';
import { Truck, User, Ban, Wrench, AlertTriangle, GraduationCap, Plane, Coffee, Sparkles, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY } from '../../store/stableEmpties';
import { VEHICLES } from '../../data/vehicles';
import { DRIVERS } from '../../data/drivers';
import { getDriverPhoto } from '../../utils/driverPhoto';

// Map raw vehicle.status → semantic bucket + visual config.
const VEHICLE_STATUS_DEF = {
  idle:        { bucket: 'available',   label: 'Disponible',   color: 'emerald', Icon: null },
  active:      { bucket: 'mission',     label: 'En mission',   color: 'amber',   Icon: null },
  delivery:    { bucket: 'mission',     label: 'Livraison',    color: 'sky',     Icon: null },
  returning:   { bucket: 'mission',     label: 'Retour',       color: 'amber',   Icon: null },
  transfer:    { bucket: 'mission',     label: 'Transfert',    color: 'purple',  Icon: null },
  maintenance: { bucket: 'unavailable', label: 'Maintenance',  color: 'orange',  Icon: Wrench },
  broken:      { bucket: 'unavailable', label: 'En panne',     color: 'rose',    Icon: Ban },
  selling:     { bucket: 'unavailable', label: 'En vente',     color: 'slate',   Icon: AlertTriangle },
};

const DRIVER_STATUS_DEF = {
  idle:      { bucket: 'available',   label: 'Disponible',   color: 'emerald', Icon: null },
  active:    { bucket: 'mission',     label: 'En mission',   color: 'amber',   Icon: null },
  returning: { bucket: 'mission',     label: 'Retour',       color: 'amber',   Icon: null },
  training:  { bucket: 'training',    label: 'En formation', color: 'sky',     Icon: GraduationCap },
  resting:   { bucket: 'unavailable', label: 'Au repos',     color: 'orange',  Icon: Coffee },
  vacation:  { bucket: 'unavailable', label: 'En congé',     color: 'purple',  Icon: Plane },
  // 10/02/2026 (Lot N P0) — Retour user prod : « ils apparaissent tous
  // comme disponibles au lieu d'être en congés sur le tableau de bord ».
  // `status: 'holiday'` (utilisé par sendDriverOnHoliday/triggerAutoHoliday)
  // n'était pas mappé → fallback sur 'idle' = bucket 'available' = bug.
  holiday:   { bucket: 'unavailable', label: 'En congés',    color: 'purple',  Icon: Plane },
  sick:      { bucket: 'unavailable', label: 'Maladie',      color: 'rose',    Icon: Ban },
  striking:  { bucket: 'unavailable', label: 'En grève',     color: 'rose',    Icon: Ban },
  on_break:  { bucket: 'unavailable', label: 'Suspendu',     color: 'orange',  Icon: Coffee },
};

// 10/07/2026 — retour user prod : distinguer le TYPE d'engagement d'un
// camion/chauffeur actif (contrat roulant, marché AO gagné, mission flash,
// convoi exceptionnel) au lieu d'un « En mission » générique.
function engagementLabel(entity, contractsById) {
  if (!entity) return null;
  if (entity.convoyExceptionnelMissionId) return 'Convoi excep.';
  if (entity.commercialOpportunityId) return 'Mission flash';
  const c = entity.assignedContractId ? contractsById?.[entity.assignedContractId] : null;
  if (c?.tenderWon) return 'Marché AO';
  if (c?.spotCallEngagementId || c?.spotCall) return 'Mission spot';
  return null;
}

// 10/07/2026 — statut effectif chauffeur (anti-drift renforcé) : un chauffeur
// référencé par un contrat actif (contractByDriverId) OU rattaché à un
// véhicule est « En mission » même si son status a dérivé vers 'idle'.
function effectiveDriverDef(driver, contractByDriverId) {
  const effectiveStatus = driver.onHoliday ? 'holiday' : driver.status;
  let def = DRIVER_STATUS_DEF[effectiveStatus];
  if (!def) {
    return { bucket: 'unavailable', label: `Statut: ${effectiveStatus || 'inconnu'}`, color: 'slate', Icon: AlertTriangle };
  }
  if (def.bucket === 'available' && (driver.assignedVehicleId || contractByDriverId?.[driver.id])) {
    return { ...DRIVER_STATUS_DEF.active, label: 'En mission' };
  }
  return def;
}

function classForColor(color, bucket) {
  // Bucket adapts opacity/saturation : 'unavailable' is muted.
  const muted = bucket === 'unavailable';
  const map = {
    emerald: { border: 'border-emerald-500/60', bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-300' },
    amber:   { border: 'border-amber-500/60',   bg: 'bg-amber-500/10',   text: 'text-amber-700 dark:text-amber-300' },
    sky:     { border: 'border-sky-500/60',     bg: 'bg-sky-500/10',     text: 'text-sky-700 dark:text-sky-300' },
    purple:  { border: 'border-purple-500/60',  bg: 'bg-purple-500/10',  text: 'text-purple-700 dark:text-purple-300' },
    orange:  { border: 'border-orange-500/60',  bg: 'bg-orange-500/10',  text: 'text-orange-700 dark:text-orange-300' },
    rose:    { border: 'border-rose-500/60',    bg: 'bg-rose-500/10',    text: 'text-rose-700 dark:text-rose-300' },
    slate:   { border: 'border-slate-500/40',   bg: 'bg-slate-500/10',   text: 'text-slate-400' },
  };
  const c = map[color] || map.slate;
  return {
    border: c.border + (muted ? ' opacity-70' : ''),
    bg: c.bg,
    text: c.text,
    imageOpacity: muted ? 'opacity-40 grayscale' : '',
  };
}

function VehicleCard({ vehicle, contractsById }) {
  let def = VEHICLE_STATUS_DEF[vehicle.status] || VEHICLE_STATUS_DEF.idle;
  // 10/07/2026 — libellé d'engagement spécifique (Mission flash / Marché AO /
  // Convoi excep.) quand le camion est mobilisé.
  if (def.bucket === 'mission') {
    const engagement = engagementLabel(vehicle, contractsById);
    if (engagement) def = { ...def, label: engagement };
  }
  const c = classForColor(def.color, def.bucket);
  const catalogVehicle = VEHICLES.find(v => v.id === vehicle.dataId || v.id === vehicle.id);
  const image = vehicle.image || catalogVehicle?.image;
  const label = catalogVehicle ? `${catalogVehicle.brand} ${catalogVehicle.model}` : (vehicle.brand && vehicle.model ? `${vehicle.brand} ${vehicle.model}` : 'Véhicule');
  // 08/02/2026 — Tier reste en data (utile pour le tooltip + ARIA) mais
  // la pastille T1-T5 a été retirée de la photo : on regroupe désormais
  // les véhicules par tier dans le rendu parent (séparateur titré). Voir
  // VehiclesGrid plus bas.
  const tier = vehicle.tier || catalogVehicle?.tier || 1;
  const StatusIcon = def.Icon;
  return (
    <div
      className={`relative rounded-lg border-2 ${c.border} ${c.bg} overflow-hidden aspect-square group`}
      title={`${label} — Tier ${tier} — ${def.label}`}
      data-testid={`fleet-overview-vehicle-${vehicle.id}`}
    >
      {image
        ? <img src={image} alt={label} className={`w-full h-full object-cover ${c.imageOpacity}`} loading="lazy" />
        : <div className={`w-full h-full flex items-center justify-center bg-ink-700 ${c.imageOpacity}`}><Truck className="w-8 h-8 text-slate-400" /></div>
      }
      {/* Status pill (top-left) */}
      <div className={`absolute top-1 left-1 px-1.5 py-0.5 rounded-md text-[10px] font-mono font-bold uppercase tracking-wider ${c.bg} ${c.text} border ${c.border} backdrop-blur-sm`}>
        {def.label}
      </div>
      {/* Stop / Wrench overlay for unavailable */}
      {StatusIcon && (
        <div className="absolute inset-0 flex items-center justify-center">
          <StatusIcon className={`w-10 h-10 ${c.text} drop-shadow-lg`} />
        </div>
      )}
      {/* Hover tooltip */}
      <div className="absolute bottom-0 left-0 right-0 px-1.5 py-1 bg-black/70 text-white text-[10px] truncate opacity-0 group-hover:opacity-100 transition-opacity">
        {label}
      </div>
    </div>
  );
}

// 08/02/2026 — tierPillClass était utilisé pour la pastille T1-T5 sur les
// cartes véhicule. La pastille a été retirée (les véhicules sont
// désormais groupés par tier via VehiclesByTier + tierHeaderClass).
// Conservé inline ci-dessous sous forme de tierHeaderClass.

function DriverCard({ driver, contractsById, contractByDriverId }) {
  // 10/02/2026 (Lot N P0) — Si onHoliday=true, force le mapping vers
  // 'holiday' même si le status n'a pas été mis à jour (drift Lot L).
  // 11/02/2026 + 10/07/2026 (fix user prod « chauffeur en mission affiché
  // disponible ») — anti-drift renforcé : un chauffeur référencé par un
  // contrat ACTIF (contract.driverId) est « En mission » même sans
  // assignedVehicleId. Logique centralisée dans effectiveDriverDef.
  let def = effectiveDriverDef(driver, contractByDriverId);
  // 10/07/2026 — libellé d'engagement spécifique quand il est mobilisé.
  if (def.bucket === 'mission') {
    const viaContract = contractByDriverId?.[driver.id];
    const engagement = engagementLabel(driver, contractsById)
      || (viaContract?.tenderWon ? 'Marché AO' : null)
      || (viaContract?.spotCallEngagementId || viaContract?.spotCall ? 'Mission spot' : null);
    if (engagement) def = { ...def, label: engagement };
  }
  const c = classForColor(def.color, def.bucket);
  const catalogDriver = DRIVERS.find(d => d.id === driver.dataId || d.id === driver.id);
  // 07/02/2026 — Photos générées par Nano Banana, exposées via le helper
  // getDriverPhoto qui dérive l'URL `/driver-portraits/{id}.webp` depuis le
  // dataId. Avant ce fix, le widget retombait sur les initiales car
  // driver.photo n'est jamais persisté côté hireDriver (cf. hrSlice).
  const photo = driver.photo || catalogDriver?.photo || getDriverPhoto(driver) || getDriverPhoto(catalogDriver) || null;
  const name = driver.name || catalogDriver?.name || 'Chauffeur';
  // 07/02/2026 — Fallback initiales si l'image webp est absente (chauffeurs
  // acquis hors catalogue, ou portrait pas encore généré).
  const [imgFailed, setImgFailed] = useState(false);
  const showPhoto = !!photo && !imgFailed;
  // 06/02/2026 — P1 retour user prod : afficher le prénom sous le portrait
  // (en plus du tooltip hover). Le premier mot du `name` est suffisant pour
  // identifier le chauffeur à la volée dans la grille.
  const firstName = (name.split(' ')[0] || name).trim();
  const initials = name.split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  const StatusIcon = def.Icon;
  // 08/02/2026 — Q2 user prod : afficher les permis du chauffeur (B, C, CE,
  // ADR, FIMO, CACES...) en dessous du portrait pour qu'on identifie d'un
  // coup d'œil ses qualifications. licenses peut être string "CE+ADR" ou
  // array — on normalise. On affiche au max 3 tags pour ne pas casser
  // la grille.
  const licenseTags = normalizeLicenses(driver.licenses || catalogDriver?.licenses);
  return (
    <div
      className="flex flex-col items-stretch gap-1"
      data-testid={`fleet-overview-driver-${driver.id}`}
    >
      <div
        className={`relative rounded-lg border-2 ${c.border} ${c.bg} overflow-hidden aspect-square group`}
        title={`${name} — ${def.label}${licenseTags.length ? ` — Permis : ${licenseTags.join(' · ')}` : ''}`}
      >
        {showPhoto
          ? <img src={photo} alt={name} className={`w-full h-full object-cover ${c.imageOpacity}`} loading="lazy" onError={() => setImgFailed(true)} />
          : (
            <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-500 to-slate-700 ${c.imageOpacity}`}>
              <span className="text-2xl font-bold font-mono text-white drop-shadow-sm">{initials}</span>
            </div>
          )
        }
        {/* Status pill */}
        <div className={`absolute top-1 left-1 px-1.5 py-0.5 rounded-md text-[10px] font-mono font-bold uppercase tracking-wider ${c.bg} ${c.text} border ${c.border} backdrop-blur-sm`}>
          {def.label}
        </div>
        {/* Status overlay icon for non-active states */}
        {StatusIcon && (
          <div className="absolute inset-0 flex items-center justify-center">
            <StatusIcon className={`w-9 h-9 ${c.text} drop-shadow-lg`} />
          </div>
        )}
        {/* Hover tooltip — full name */}
        <div className="absolute bottom-0 left-0 right-0 px-1.5 py-1 bg-black/70 text-white text-[10px] truncate opacity-0 group-hover:opacity-100 transition-opacity">
          {name}
        </div>
      </div>
      {/* First-name label — always visible under the portrait. */}
      <span
        className="block text-[11px] font-semibold text-slate-200 text-center truncate px-0.5"
        data-testid={`fleet-overview-driver-name-${driver.id}`}
      >
        {firstName}
      </span>
      {/* 08/02/2026 — License tags row under the name (up to 3). */}
      {licenseTags.length > 0 && (
        <div
          className="flex flex-wrap justify-center gap-0.5 px-0.5"
          data-testid={`fleet-overview-driver-${driver.id}-licenses`}
        >
          {licenseTags.slice(0, 3).map((lic) => (
            <span
              key={lic}
              className={`text-[10px] font-mono font-bold uppercase tracking-wider px-1 py-px rounded border ${licenseTagClass(lic)}`}
              title={`Permis ${lic}`}
            >
              {lic}
            </span>
          ))}
          {licenseTags.length > 3 && (
            <span className="text-[10px] font-mono text-slate-400">+{licenseTags.length - 3}</span>
          )}
        </div>
      )}
    </div>
  );
}

// 08/02/2026 — Normalisation des permis driver.licenses (peut être string
// "CE+ADR" ou array). Renvoie un array de chaînes courtes.
function normalizeLicenses(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  return String(raw).split(/[+,;\s\/]/).map(s => s.trim()).filter(Boolean);
}

// 08/02/2026 — Couleurs des badges permis.
function licenseTagClass(lic) {
  const norm = String(lic).toUpperCase();
  if (norm === 'ADR') return 'bg-rose-500/25 text-rose-100 border-rose-400/50';
  if (norm === 'CE')  return 'bg-amber-500/25 text-amber-100 border-amber-400/50';
  if (norm === 'C')   return 'bg-sky-500/25 text-sky-100 border-sky-400/50';
  if (norm === 'B')   return 'bg-emerald-500/25 text-emerald-100 border-emerald-400/50';
  if (norm === 'FIMO' || norm === 'FCO') return 'bg-violet-500/25 text-violet-100 border-violet-400/50';
  if (norm.startsWith('CACES')) return 'bg-orange-500/25 text-orange-100 border-orange-400/50';
  return 'bg-slate-500/25 text-slate-100 border-slate-400/50';
}

function CountChip({ label, count, tone, active, onClick, testId }) {
  const map = {
    emerald: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
    amber:   'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
    sky:     'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40',
    rose:    'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40',
  };
  const activeMap = {
    emerald: 'bg-emerald-500/40 text-emerald-100 border-emerald-400 ring-2 ring-emerald-400/60',
    amber:   'bg-amber-500/40 text-amber-100 border-amber-400 ring-2 ring-amber-400/60',
    sky:     'bg-sky-500/40 text-sky-100 border-sky-400 ring-2 ring-sky-400/60',
    rose:    'bg-rose-500/40 text-rose-100 border-rose-400 ring-2 ring-rose-400/60',
  };
  const classes = active ? (activeMap[tone] || activeMap.emerald) : (map[tone] || map.emerald);
  const baseCls = `inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono font-bold transition-all ${classes}`;
  if (!onClick) {
    return (
      <span className={baseCls}>
        <span>{count}</span>
        <span className="opacity-70">{label}</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseCls} cursor-pointer hover:opacity-90 active:scale-95 ${active ? '' : 'hover:ring-1 hover:ring-current/40'}`}
      data-testid={testId}
      aria-pressed={!!active}
    >
      <span>{count}</span>
      <span className="opacity-80">{label}</span>
    </button>
  );
}

// 08/02/2026 — Helpers de groupement par tier (véhicules) et permis principal
// (chauffeurs). Les sections sont collapsibles individuellement et leur
// affichage initial est limité à `INITIAL_PER_SECTION` cartes pour ne pas
// faire exploser le widget sur un dashboard chargé (cohorte de 40+ camions).
const INITIAL_PER_SECTION = 6;

function groupVehiclesByTier(list) {
  // Tier 1 → 5 ascending. On regroupe + on trie chaque groupe par status
  // (dispo → mission → indispo) pour aider à scanner les anomalies.
  const buckets = {};
  list.forEach(v => {
    const cat = VEHICLES.find(c => c.id === v.dataId || c.id === v.id);
    const tier = v.tier || cat?.tier || 1;
    if (!buckets[tier]) buckets[tier] = [];
    buckets[tier].push(v);
  });
  const STATUS_ORDER = { idle: 0, active: 1, delivery: 1, returning: 1, transfer: 1, maintenance: 2, broken: 2, selling: 3 };
  return Object.keys(buckets)
    .map(Number)
    .sort((a, b) => a - b)
    .map(tier => ({
      tier,
      items: buckets[tier].sort((a, b) => (STATUS_ORDER[a.status] || 9) - (STATUS_ORDER[b.status] || 9)),
    }));
}

function tierHeaderClass(tier) {
  switch (tier) {
    case 5: return 'bg-fuchsia-500/20 text-fuchsia-100 border-fuchsia-400/60';
    case 4: return 'bg-violet-500/20 text-violet-100 border-violet-400/60';
    case 3: return 'bg-amber-500/20 text-amber-100 border-amber-400/60';
    case 2: return 'bg-sky-500/20 text-sky-100 border-sky-400/60';
    default: return 'bg-slate-500/20 text-slate-100 border-slate-400/60';
  }
}

const TIER_DESCRIPTOR = {
  1: 'Léger (B / VL)',
  2: 'Moyen (C)',
  3: 'Lourd (CE)',
  4: 'Spécialisé (ADR / convoi)',
  5: 'Très lourd / international',
};

function VehiclesByTier({ filteredVehicles, expanded, onToggle, contractsById }) {
  const groups = useMemo(() => groupVehiclesByTier(filteredVehicles), [filteredVehicles]);
  if (groups.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="fleet-overview-vehicles-by-tier">
      {groups.map(({ tier, items }) => {
        const isExpanded = expanded[tier] ?? false;
        const visible = isExpanded ? items : items.slice(0, INITIAL_PER_SECTION);
        const hidden = items.length - visible.length;
        return (
          <div key={`tier-${tier}`} data-testid={`fleet-overview-tier-section-${tier}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-mono font-black tracking-wider border ${tierHeaderClass(tier)}`}
                data-testid={`fleet-overview-tier-header-${tier}`}
              >
                TIER {tier}
              </span>
              <span className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">
                {TIER_DESCRIPTOR[tier]} · {items.length} camion{items.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
              {visible.map(v => <VehicleCard key={v.id} vehicle={v} contractsById={contractsById} />)}
            </div>
            {hidden > 0 && (
              <button
                type="button"
                onClick={() => onToggle(tier)}
                className="mt-1.5 text-[10px] uppercase tracking-wider text-slate-400 hover:text-foreground inline-flex items-center gap-1"
                data-testid={`fleet-overview-tier-expand-${tier}`}
              >
                <ChevronDown className="w-3 h-3" /> Voir les {hidden} autre{hidden > 1 ? 's' : ''}
              </button>
            )}
            {isExpanded && items.length > INITIAL_PER_SECTION && (
              <button
                type="button"
                onClick={() => onToggle(tier)}
                className="mt-1.5 text-[10px] uppercase tracking-wider text-slate-400 hover:text-foreground inline-flex items-center gap-1"
                data-testid={`fleet-overview-tier-collapse-${tier}`}
              >
                <ChevronUp className="w-3 h-3" /> Réduire
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function FleetOverviewWidget() {
  const vehicles = useGameStore(s => s.vehicles || EMPTY_ARRAY);
  const drivers = useGameStore(s => s.drivers || EMPTY_ARRAY);
  const activeContracts = useGameStore(s => s.activeContracts || EMPTY_ARRAY);
  // 10/07/2026 — index des contrats actifs pour les libellés d'engagement
  // (Marché AO / Mission spot) et l'anti-drift chauffeur↔contrat.
  const { contractsById, contractByDriverId } = useMemo(() => {
    const byId = {};
    const byDriver = {};
    activeContracts.forEach(c => {
      if (!c) return;
      if (c.instanceId) byId[c.instanceId] = c;
      if (c.driverId) byDriver[c.driverId] = c;
    });
    return { contractsById: byId, contractByDriverId: byDriver };
  }, [activeContracts]);
  // 07/02/2026 — P1 retour user : filtres cliquables (1 seul filtre actif
  // par section). Cliquer un chip déjà actif désactive le filtre.
  const [vehicleFilter, setVehicleFilter] = useState(null);
  const [driverFilter, setDriverFilter] = useState(null);
  // 08/02/2026 — Q2 user prod : agrandir progressivement l'encart.
  // `tierExpanded` est un map { [tier]: true|false } pour chaque section
  // de tier. État par défaut : tous repliés (montre INITIAL_PER_SECTION).
  const [tierExpanded, setTierExpanded] = useState({});
  const toggleTier = (tier) => setTierExpanded(prev => ({ ...prev, [tier]: !prev[tier] }));

  // Aggregate counts per bucket.
  const vehicleCounts = vehicles.reduce(
    (acc, v) => {
      const b = (VEHICLE_STATUS_DEF[v.status] || VEHICLE_STATUS_DEF.idle).bucket;
      acc[b] = (acc[b] || 0) + 1;
      return acc;
    },
    { available: 0, mission: 0, unavailable: 0 },
  );
  const driverCounts = drivers.reduce(
    (acc, d) => {
      // 10/02/2026 (Lot N P0) + 10/07/2026 — même logique que DriverCard
      // (effectiveDriverDef) pour compter dans le bon bucket.
      const b = effectiveDriverDef(d, contractByDriverId).bucket;
      acc[b] = (acc[b] || 0) + 1;
      return acc;
    },
    { available: 0, mission: 0, training: 0, unavailable: 0 },
  );

  const filteredVehicles = vehicleFilter
    ? vehicles.filter(v => (VEHICLE_STATUS_DEF[v.status] || VEHICLE_STATUS_DEF.idle).bucket === vehicleFilter)
    : vehicles;
  const filteredDrivers = driverFilter
    ? drivers.filter(d => effectiveDriverDef(d, contractByDriverId).bucket === driverFilter)
    : drivers;
  const toggleVehicleFilter = (b) => setVehicleFilter(curr => (curr === b ? null : b));
  const toggleDriverFilter = (b) => setDriverFilter(curr => (curr === b ? null : b));

  // Empty-state.
  if (vehicles.length === 0 && drivers.length === 0) {
    return (
      <div className="surface-card p-6 text-center" data-testid="fleet-overview-empty">
        <Sparkles className="w-8 h-8 text-slate-500 mx-auto mb-2" />
        <p className="text-sm text-slate-500">
          Aucun véhicule ni chauffeur pour l&apos;instant. Lance le premier achat sur la page Flotte&nbsp;!
        </p>
      </div>
    );
  }

  return (
    <div className="surface-card overflow-hidden" data-testid="fleet-overview-widget">
      <div className="px-4 py-3 border-b border-ink-600 flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase">
          Vue d&apos;ensemble flotte &amp; équipe
        </h3>
        <span className="text-[10px] text-slate-500 font-mono">
          {vehicles.length} camion{vehicles.length > 1 ? 's' : ''} · {drivers.length} chauffeur{drivers.length > 1 ? 's' : ''}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-ink-600">
        {/* ── Vehicles ── */}
        <section className="p-4" data-testid="fleet-overview-vehicles">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-slate-500" />
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Camions</h4>
              {vehicleFilter && (
                <button
                  type="button"
                  onClick={() => setVehicleFilter(null)}
                  className="text-[10px] text-slate-500 hover:text-foreground inline-flex items-center gap-1"
                  data-testid="fleet-overview-vehicle-clear-filter"
                  title="Effacer le filtre"
                >
                  <XCircle className="w-3 h-3" /> filtre
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <CountChip label="dispo" count={vehicleCounts.available} tone="emerald" active={vehicleFilter === 'available'} onClick={() => toggleVehicleFilter('available')} testId="fleet-overview-vehicle-chip-available" />
              <CountChip label="en mission" count={vehicleCounts.mission} tone="amber" active={vehicleFilter === 'mission'} onClick={() => toggleVehicleFilter('mission')} testId="fleet-overview-vehicle-chip-mission" />
              <CountChip label="indispo" count={vehicleCounts.unavailable} tone="rose" active={vehicleFilter === 'unavailable'} onClick={() => toggleVehicleFilter('unavailable')} testId="fleet-overview-vehicle-chip-unavailable" />
            </div>
          </div>
          {filteredVehicles.length === 0 ? (
            <p className="text-xs text-slate-500 italic py-4">
              {vehicleFilter
                ? 'Aucun camion ne correspond au filtre.'
                : 'Aucun camion en flotte.'}
            </p>
          ) : (
            <VehiclesByTier
              filteredVehicles={filteredVehicles}
              expanded={tierExpanded}
              onToggle={toggleTier}
              contractsById={contractsById}
            />
          )}
        </section>

        {/* ── Drivers ── */}
        <section className="p-4" data-testid="fleet-overview-drivers">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-500" />
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Chauffeurs</h4>
              {driverFilter && (
                <button
                  type="button"
                  onClick={() => setDriverFilter(null)}
                  className="text-[10px] text-slate-500 hover:text-foreground inline-flex items-center gap-1"
                  data-testid="fleet-overview-driver-clear-filter"
                  title="Effacer le filtre"
                >
                  <XCircle className="w-3 h-3" /> filtre
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <CountChip label="dispo" count={driverCounts.available} tone="emerald" active={driverFilter === 'available'} onClick={() => toggleDriverFilter('available')} testId="fleet-overview-driver-chip-available" />
              <CountChip label="en mission" count={driverCounts.mission} tone="amber" active={driverFilter === 'mission'} onClick={() => toggleDriverFilter('mission')} testId="fleet-overview-driver-chip-mission" />
              <CountChip label="formation" count={driverCounts.training} tone="sky" active={driverFilter === 'training'} onClick={() => toggleDriverFilter('training')} testId="fleet-overview-driver-chip-training" />
              <CountChip label="indispo" count={driverCounts.unavailable} tone="rose" active={driverFilter === 'unavailable'} onClick={() => toggleDriverFilter('unavailable')} testId="fleet-overview-driver-chip-unavailable" />
            </div>
          </div>
          {filteredDrivers.length === 0 ? (
            <p className="text-xs text-slate-500 italic py-4">
              {driverFilter
                ? 'Aucun chauffeur ne correspond au filtre.'
                : 'Aucun chauffeur recruté.'}
            </p>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
              {filteredDrivers.map(d => <DriverCard key={d.id} driver={d} contractsById={contractsById} contractByDriverId={contractByDriverId} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
